import { useNavigate, useParams } from '@remix-run/react'
import { getApp } from 'firebase/app'
import { getAuth, signOut } from 'firebase/auth'
import type React from 'react'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import users from '~/consts/users'
import useFeatureFlags from '~/hooks/useFeatureFlags'
import type { TechDiscUser } from '~/types/user'
import { handleAuth } from '~/utils/auth.client'

export interface AuthContextType {
    user?: TechDiscUser | null
    externalUserId?: string
    publicUserId: string
    signOut: () => void
    prefersMetric: boolean
    isLiveAccount: boolean
    userLoading: boolean
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useUser = (): AuthContextType => {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useUser must be used within a AuthProvider')
    }
    return context
}

interface AuthProviderProps {
    children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<TechDiscUser | null>(null)
    const [loading, setLoading] = useState(true)
    const auth = getAuth(getApp())

    const navigate = useNavigate()

    const handleSignOut = useCallback(async () => {
        await signOut(auth)
        setUser(null)
        navigate('/login')
    }, [navigate, setUser])

    useEffect(() => {
        return handleAuth(setUser, setLoading)
    }, [setLoading, setUser])

    const featureFlags = useFeatureFlags(user)
    const { userId: externalUserId } = useParams()
    const publicUserId = featureFlags.live_event ? users.LIVE_USER : 'unknown'
    const prefersMetric = user?.settings?.preferredMeasurementSystem === 'metric'
    const isLiveAccount = user?.uid === users.LIVE_USER

    return (
        <AuthContext.Provider
            value={{
                user,
                signOut: handleSignOut,
                externalUserId,
                publicUserId,
                prefersMetric,
                isLiveAccount,
                userLoading: loading,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}
